import { Typography } from '../../../components/typography/typography';
import { useRef } from 'react';
import { Toggle } from '../../../components/form/toggle';
import Icon from '../../../components/icons/icon';
import classNames from 'classnames';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { Location } from '../../../clients/quipli-service/location/location';
import { EmailGroup, EmailItem, emailsGroup } from '../config/emails';
import { SelectForm } from '../../../components/select-form/select-form';
import { components } from 'react-select';
import { AdminViewContext } from './admin-user-view';
import { useOutletContext } from 'react-router-dom';
import { Card } from '../../../components/card/card';
import { Collapsible, CollapsibleRef } from '../../../components/collapsible/collapsible';
import { useAdminLocations } from '../hooks/use-admin-locations';
import { useAdminEmailNotifications } from '../hooks/use-admin-email-notifications';
import { Divider } from '../../../components/divider/divider';
import SkeletonLoader from '../../../components/skeleton-loader/skeleton-loader';
import { handleLocationSelection } from '../utils/email-notifications-utils';
import {
  EmailNotificationsSelectComponents,
  emailNotificationsSelectStyles,
} from '../components/email-notifications-select';

export const AdminUserEmailNotification = () => {
  const { admin } = useOutletContext<AdminViewContext>();

  const { isLoading: isLocationsLoading, selectedLocations } = useAdminLocations({ adminId: admin?.adminId });

  const {
    isLoading: isEmailsLoading,
    isSubmitting,
    toggleEmailForLocation,
    toggleAllEmailsForLocation,
    areAllEmailsSelectedForLocation,
    isEmailSelectedForLocation,
    toggleAllEmailsForAllLocations,
    areAllEmailsSelectedForAllLocations,
  } = useAdminEmailNotifications({
    adminId: admin?.adminId,
    adminLocations: selectedLocations.map((location) => location.id),
  });

  // Create refs for each location's collapsible
  const collapsibleRefs = useRef<{ [key: number]: CollapsibleRef }>({});

  return (
    <div className="tab-section">
      <div className="tab-title">
        <div className="w-full">
          <div className="flex w-full justify-between items-center">
            <Typography type="text-xl" weight="semi-bold">
              Email Notifications
            </Typography>
            <div className="flex items-center">
              <div className="mb-1 mr-2" data-cy="global-select-all-label">
                <span className="font-sans text-sm text-black-800">Select All</span>
              </div>
              <Toggle
                color="var(--quipli-yellow-300)"
                disabled={isSubmitting}
                onClick={toggleAllEmailsForAllLocations}
                checked={areAllEmailsSelectedForAllLocations()}
                data-cy="global-select-all-toggle"
              />
            </div>
          </div>

          <Typography type="text-xs" weight="regular" className="text-gray-500">
            Turn on/off admin emails by store location
          </Typography>
        </div>
      </div>

      <div className="w-full md:w-auto mb-5">
        <SelectForm<Location, false>
          isClearable
          isLoading={isLocationsLoading}
          options={selectedLocations}
          getOptionLabel={(op) => `${op.name}`}
          getOptionValue={(op) => `${op.id}`}
          filterOption={(op, inputValue) => op.data.name.toLowerCase().includes(inputValue.toLowerCase())}
          onChange={(location) => handleLocationSelection(location, collapsibleRefs.current)}
          dataCy="search_location"
          styles={emailNotificationsSelectStyles}
          components={{
            Placeholder: EmailNotificationsSelectComponents.Placeholder,
            SingleValue: EmailNotificationsSelectComponents.SingleValue,
            Option: (props) => {
              const { name } = props.data;

              return (
                <div className={classNames('select-group-child')}>
                  <components.Option {...props}>
                    <div className="flex items-center justify-between">
                      <Typography type="text-sm" className="text-quipli-black-800 flex items-center gap-y-10">
                        {name}
                      </Typography>
                    </div>
                  </components.Option>
                </div>
              );
            },
          }}
        />
      </div>

      <Card className="!px-0 !py-1 !shadow-none !border-[1px] overflow-hidden">
        {isLocationsLoading || isEmailsLoading ? (
          <div className="p-[16px] w-full" data-cy="locations-skeleton-loader">
            <SkeletonLoader num={2} style={{ width: '100%', height: '3rem' }} />
          </div>
        ) : (
          selectedLocations.map((location: Location, idx: number) => (
            <Collapsible
              key={idx}
              ref={(ref) => {
                collapsibleRefs.current[location.id] = ref;
              }}
              trigger={(isOpen) => (
                <>
                  {idx > 0 && <Divider data-cy="divider" />}

                  <div
                    id={`list-item-header-${location.id}`}
                    data-cy={`list-item-header-${location.id}`}
                    key={idx}
                    className="bg-white p-[16px] flex justify-between items-center"
                  >
                    <Typography type="text-md" weight="semi-bold" className="text-grey-800">
                      {location.name}
                    </Typography>

                    <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" />
                  </div>
                </>
              )}
            >
              <div key={`content-${idx}`} className={classNames('flex bg-white p-[16px] flex-col collapse-transition')}>
                <div className="flex w-full mb-2">
                  <div className="mb-1 mr-2" data-cy={`location-select-all-label-${location.id}`}>
                    <span className="font-sans text-sm text-black-800">Select All</span>
                  </div>
                  <Toggle
                    color="var(--quipli-yellow-300)"
                    disabled={isSubmitting}
                    onClick={() => toggleAllEmailsForLocation(location.id)}
                    checked={areAllEmailsSelectedForLocation(location.id)}
                    data-cy={`location-select-all-toggle-${location.id}`}
                  />
                </div>
                {emailsGroup.map((elem: EmailGroup, index: number) => (
                  <div key={idx + '-' + index} className="flex-col mb-2">
                    <Typography type="text-sm" weight="semi-bold" className="text-grey-700 mb-2">
                      {elem.module}
                    </Typography>
                    {elem.emails.map((email: EmailItem, i: number) => (
                      <div key={idx + '-' + index + '-' + i} className="flex mb-2">
                        <Checkbox
                          isChecked={isEmailSelectedForLocation(location.id, email.key)}
                          onChange={(isChecked) => {
                            toggleEmailForLocation(email.key, location.id, isChecked);
                          }}
                          isDisabled={isSubmitting}
                          dataCy={`checkbox-${location.id}-${email.key}`}
                        />
                        <Typography type="text-sm" weight="regular" className="text-grey-700">
                          {email.label}
                        </Typography>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Collapsible>
          ))
        )}
      </Card>
    </div>
  );
};
